import React from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/shared/components/ui/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Copy,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Zap,
  Users,
  Building2,
  Webhook,
  Phone,
  RefreshCw,
  Edit,
} from "lucide-react";
import DrillDownBreadcrumb from "./DrillDownBreadcrumb";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as transactionService from "@/shared/api/services/transactionService";

const statusConfigDefault = {
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Processing" },
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Success" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
  expired: { icon: Clock, color: "bg-slate-100 text-slate-700", label: "Expired" },
  cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Cancelled" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: t("processing") },
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("success") },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("failed") },
    expired: { icon: Clock, color: "bg-slate-100 text-slate-700", label: t("expired") },
    cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("cancelled") },
    declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("declined") || "Declined" },
  };
};

export default function TransactionDetailPanel({ 
  transaction, 
  open, 
  onClose, 
  engine, 
  reseller, 
  merchant,
  isQRCode = false,
}) {
  const [copied, setCopied] = React.useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = React.useState(false);
  const [newStatus, setNewStatus] = React.useState(
    transaction?.status?.toUpperCase() || "PENDING"
  );
  const [adminNote, setAdminNote] = React.useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  let t = null;
  try {
    const langContext = useLanguage();
    t = langContext?.t;
  } catch (e) {
    // Not wrapped in LanguageProvider
  }
  const statusConfig = getStatusConfig(t);

  // Helper to translate transaction type
  const getTransactionTypeLabel = (type) => {
    if (!t) return type;
    const typeUpper = (type || '').toUpperCase();
    if (typeUpper === "DEPOSIT") {
      return t("deposit");
    } else if (typeUpper === "WITHDRAWAL") {
      return t("withdrawal");
    }
    return type;
  };

  // Reset form when transaction changes
  React.useEffect(() => {
    if (transaction) {
      setNewStatus(transaction.status?.toUpperCase() || "PENDING");
      setAdminNote("");
      setShowStatusUpdate(false);
    }
  }, [transaction?.id]);

  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      return transactionService.updateTransactionStatus(
        transaction.id,
        newStatus,
        adminNote || undefined
      );
    },
    onSuccess: (updatedTransaction) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries(["all-transactions"]);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["transactionById", transaction.id]);
      
      // Update local transaction if parent component manages state
      if (transaction) {
        Object.assign(transaction, updatedTransaction);
      }
      
      toast({
        title: t ? t("statusUpdated") : "Status updated",
        description: t ? `${t("transactionStatusChangedTo")} ${t(newStatus.toLowerCase()) || newStatus}` : `Transaction status changed to ${newStatus}`,
      });
      
      setShowStatusUpdate(false);
      setAdminNote("");
    },
    onError: (error) => {
      toast({
        title: t ? t("updateFailed") : "Update failed",
        description: error.message || (t ? t("failedToUpdateTransactionStatus") : "Failed to update transaction status"),
        variant: "destructive",
      });
    },
  });

  const copyId = () => {
    const idToCopy = isQRCode ? transaction?.payment_id : transaction?.transaction_id;
    navigator.clipboard.writeText(idToCopy || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!transaction) return null;

  const statusKey = (transaction?.status || '').toLowerCase();
  const status = statusConfig[statusKey] || statusConfig.pending;
  const StatusIcon = status.icon;

  const statusTimeline = [
    { status: "created", label: t ? (t("createdLabel") || t("created")) : "Created", time: transaction.created_date, done: true },
    { 
      status: "processing", 
      label: t ? t("processing") : "Processing", 
      time: transaction.processed_at,
      done: ["processing", "success", "failed"].includes(transaction.status)
    },
    { 
      status: transaction.status === "failed" ? "failed" : "success", 
      label: transaction.status === "failed" ? (t ? t("failed") : "Failed") : (t ? t("completed") : "Completed"),
      time: transaction.completed_at,
      done: ["success", "failed"].includes(transaction.status)
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
              <ArrowDownToLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-lg">{isQRCode ? (t ? t("qrCodePayment") : "QR Code Payment") : (getTransactionTypeLabel(transaction.type) || (t ? t("transaction") : "Transaction"))}</p>
              <Badge className={`${status.color} gap-1 border-0`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </Badge>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Drill-Down Hierarchy */}
          <DrillDownBreadcrumb 
            engine={engine} 
            reseller={reseller} 
            merchant={merchant}
            transaction={transaction}
          />

          {/* Amount */}
          <div className="text-center py-4 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-500 mb-1">{t ? t("amount") : "Amount"}</p>
            <p className="text-3xl font-bold text-blue-600">
              ฿{isQRCode ? (transaction.amount?.toLocaleString()) : (transaction.amount?.toLocaleString())}
            </p>
          </div>

          {/* Payment ID / Transaction ID */}
          <div>
            <p className="text-sm text-slate-500 mb-2">{isQRCode ? (t ? t("paymentId") || "Payment ID" : "Payment ID") : (t ? t("transactionId") : "Transaction ID")}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm truncate">
                {isQRCode ? transaction.payment_id : transaction.transaction_id}
              </code>
              <Button variant="outline" size="icon" onClick={copyId}>
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Reference (QR Code only) */}
          {isQRCode && transaction.reference && (
            <div>
              <p className="text-sm text-slate-500 mb-2">{t ? t("reference") : "Reference"}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm truncate">
                  {transaction.reference}
                </code>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="grid grid-cols-3 gap-2">
            {isQRCode && transaction.reseller && (
              <Link to={createPageUrl("AdminResellerDetail") + `?id=${transaction.reseller.id}`}>
                <Button variant="outline" className="w-full gap-2 h-auto py-3 flex-col">
                  <Users className="h-4 w-4 text-purple-600" />
                  <span className="text-xs">{t ? (t("reseller") || t("resellers")) : "Reseller"}</span>
                </Button>
              </Link>
            )}
            {!isQRCode && engine && (
              <Link to={createPageUrl("AdminEngineDetail") + `?id=${engine.id}`}>
                <Button variant="outline" className="w-full gap-2 h-auto py-3 flex-col">
                  <Zap className="h-4 w-4 text-sky-600" />
                  <span className="text-xs">{t ? (t("engine") || t("engines")) : "Engine"}</span>
                </Button>
              </Link>
            )}
            {!isQRCode && reseller && (
              <Link to={createPageUrl("AdminResellerDetail") + `?id=${reseller.id}`}>
                <Button variant="outline" className="w-full gap-2 h-auto py-3 flex-col">
                  <Users className="h-4 w-4 text-purple-600" />
                  <span className="text-xs">{t ? (t("reseller") || t("resellers")) : "Reseller"}</span>
                </Button>
              </Link>
            )}
            {merchant && (
              <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id || transaction.merchant_id}`}>
                <Button variant="outline" className="w-full gap-2 h-auto py-3 flex-col">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <span className="text-xs">{t ? t("merchant") : "Merchant"}</span>
                </Button>
              </Link>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {isQRCode ? (
              <>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("paymentId") || "Payment ID" : "Payment ID"}</p>
                  <p className="font-medium font-mono text-sm">{transaction.payment_id || (t ? t("nA") : "N/A")}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("provider") : "Provider"}</p>
                  <p className="font-medium">{transaction.client_bank_name || (t ? t("nA") : "N/A")}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("merchantRef") || t("reference") : "Merchant Ref"}</p>
                  <p className="font-medium font-mono text-sm">{transaction.reference || (t ? t("nA") : "N/A")}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("merchant") : "Merchant"}</p>
                  <p className="font-medium">{transaction.merchant?.name || transaction.merchant?.code || (t ? t("nA") : "N/A")}</p>
                </div>
                {transaction.client_name && (
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("clientName") || "Client Name" : "Client Name"}</p>
                    <p className="font-medium">{transaction.client_name}</p>
                  </div>
                )}
                {transaction.client_bank_account && (
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("clientBankAccount") || "Client Bank Account" : "Client Bank Account"}</p>
                    <p className="font-medium font-mono text-sm">{transaction.client_bank_account}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("provider") : "Provider"}</p>
                  <p className="font-medium">
                    {(() => {
                      const v = transaction.provider_code || (t ? t("nA") : "N/A");
                      return (v === "QR Code" && t) ? t("qrCode") : v;
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("merchantRef") : "Merchant Ref"}</p>
                  <p className="font-medium font-mono text-sm">{transaction.merchant_ref || (t ? t("nA") : "N/A")}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("paymentMethod") : "Payment Method"}</p>
                  <p className="font-medium capitalize">{transaction.payment_method?.replace(/_/g, " ") || (t ? t("nA") : "N/A")}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t ? t("merchant") : "Merchant"}</p>
                  <p className="font-medium">{transaction.merchant_code || (t ? t("nA") : "N/A")}</p>
                </div>
              </>
            )}
          </div>

          {/* Customer Info - Only for transactions, not QR codes */}
          {!isQRCode && (transaction.customer_name || transaction.customer_email || transaction.customer_phone) && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-700 mb-3">{t ? t("customerInfo") : "Customer Info"}</p>
              <div className="space-y-2">
                {transaction.customer_name && (
                  <p className="text-sm"><span className="text-slate-500">{t ? t("name") : "Name"}:</span> {transaction.customer_name}</p>
                )}
                {transaction.customer_email && (
                  <p className="text-sm"><span className="text-slate-500">{t ? t("email") : "Email"}:</span> {transaction.customer_email}</p>
                )}
                {transaction.customer_phone && (
                  <p className="text-sm"><span className="text-slate-500">{t ? t("phone") : "Phone"}:</span> {transaction.customer_phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Bank Details for Withdrawal - Only for transactions */}
          {!isQRCode && transaction.type === "withdrawal" && (transaction.bank_code || transaction.bank_account) && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-700 mb-3">{t ? t("bankDetails") : "Bank Details"}</p>
              <div className="space-y-2">
                <p className="text-sm"><span className="text-slate-500">{t ? t("bank") : "Bank"}:</span> {transaction.bank_code}</p>
                <p className="text-sm font-mono"><span className="text-slate-500">{t ? t("account") : "Account"}:</span> {transaction.bank_account}</p>
              </div>
            </div>
          )}

          {/* QR Code Expiry - Only for QR codes */}
          {isQRCode && transaction.expires_at && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-700 mb-3">{t ? t("qrPaymentInfo") : "QR Payment Info"}</p>
              <div className="space-y-2">
                <p className="text-sm"><span className="text-slate-500">{t ? t("expires") : "Expires"}:</span> {format(new Date(transaction.expires_at), "MMM d, yyyy HH:mm")}</p>
              </div>
            </div>
          )}

          {/* Admin Status Update Section - Only for transactions */}
          {!isQRCode && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  {t ? t("manualStatusUpdate") : "Manual Status Update"}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                  className="h-8"
                >
                  {showStatusUpdate ? (t ? t("cancel") : "Cancel") : (t ? t("updateStatus") : "Update Status")}
                </Button>
              </div>
              
              {showStatusUpdate && (
                <div className="space-y-3 pt-3 border-t border-blue-200">
                  <div>
                    <label className="text-sm font-medium text-blue-800 mb-2 block">
                      {t ? t("newStatus") : "New Status"}
                    </label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">{t ? t("pending") : "Pending"}</SelectItem>
                        <SelectItem value="PROCESSING">{t ? t("processing") : "Processing"}</SelectItem>
                        <SelectItem value="SUCCESS">{t ? t("success") : "Success"}</SelectItem>
                        <SelectItem value="FAILED">{t ? t("failed") : "Failed"}</SelectItem>
                        <SelectItem value="EXPIRED">{t ? t("expired") : "Expired"}</SelectItem>
                        <SelectItem value="CANCELLED">{t ? t("cancelled") : "Cancelled"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-blue-800 mb-2 block">
                      {t ? t("adminNoteOptional") : "Admin Note (Optional)"}
                    </label>
                    <Textarea
                      placeholder={t ? t("reasonForStatusChange") : "Reason for status change..."}
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      className="bg-white min-h-[80px]"
                      maxLength={500}
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      {adminNote.length}/500 {t ? t("characters") : "characters"}
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => updateStatusMutation.mutate()}
                    disabled={
                      updateStatusMutation.isPending || 
                      newStatus?.toUpperCase() === transaction.status?.toUpperCase()
                    }
                    className="w-full"
                  >
                    {updateStatusMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t ? t("updating") : "Updating..."}
                      </>
                    ) : (
                      t ? t("updateStatus") : "Update Status"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Admin Note Display - Only for transactions */}
          {!isQRCode && transaction.admin_note && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-700 mb-2">{t ? t("adminNote") : "Admin Note"}</p>
              <p className="text-sm text-slate-600">{transaction.admin_note}</p>
            </div>
          )}

          {/* Status Timeline - Only for transactions */}
          {!isQRCode && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-3">{t ? t("statusTimeline") : "Status Timeline"}</p>
              <div className="space-y-4">
                {statusTimeline.map((step, index) => {
                  const isLast = index === statusTimeline.length - 1;
                  const isFailed = step.status === "failed";
                  
                  return (
                    <div key={step.status} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          step.done
                            ? isFailed
                              ? "bg-red-100"
                              : "bg-emerald-100"
                            : "bg-slate-100"
                        }`}>
                          {step.done ? (
                            isFailed ? (
                              <XCircle className="h-4 w-4 text-red-600" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            )
                          ) : (
                            <Clock className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                        {!isLast && (
                          <div className={`w-0.5 h-8 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />
                        )}
                      </div>
                      <div className="flex-1 pb-2">
                        <p className={`font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}>
                          {step.label}
                        </p>
                        {step.time && step.done && (
                          <p className="text-sm text-slate-500">
                            {format(new Date(step.time), "MMM d, yyyy HH:mm:ss")}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Callback Status - Only for transactions */}
          {!isQRCode && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {t ? t("callbackStatus") : "Callback Status"}
                </p>
                <Badge className={
                  (() => {
                    const statusLower = (transaction.callback_status || "pending").toLowerCase();
                    return statusLower === "success" || statusLower === "delivered" 
                      ? "bg-emerald-100 text-emerald-700"
                      : statusLower === "failed" 
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700";
                  })()
                }>
                  {(() => {
                    const status = (transaction.callback_status || "pending").toLowerCase();
                    if (!t) {
                      if (status === "success" || status === "delivered") return "Delivered";
                      if (status === "failed") return "Failed";
                      return "Pending";
                    }
                    if (status === "success" || status === "delivered") return t("delivered") || "Delivered";
                    if (status === "failed") return t("failed") || "Failed";
                    if (status === "pending") return t("pending") || "Pending";
                    return status;
                  })()}
                </Badge>
              </div>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">{t ? t("url") : "URL"}:</span> <span className="font-mono text-xs break-all">{transaction.callback_url || (t ? t("nA") : "N/A")}</span></p>
                <p><span className="text-slate-500">{t ? t("attempts") : "Attempts"}:</span> {transaction.callback_attempts || 0}</p>
                {transaction.callback_last_attempt && (
                  <p><span className="text-slate-500">{t ? t("lastAttempt") : "Last Attempt"}:</span> {format(new Date(transaction.callback_last_attempt), "MMM d, HH:mm:ss")}</p>
                )}
              </div>
            </div>
          )}

          {/* Webhook Status - Only for transactions */}
          {!isQRCode && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Webhook className="h-4 w-4" />
                  {t ? t("webhookInfo") : "Webhook Info"}
                </p>
              </div>
              <div className="text-sm space-y-1">
                {transaction.webhook_received_at && (
                  <p><span className="text-slate-500">{t ? t("received") : "Received"}:</span> {format(new Date(transaction.webhook_received_at), "MMM d, HH:mm:ss")}</p>
                )}
                <p><span className="text-slate-500">{t ? t("providerRef") : "Provider Ref"}:</span> {transaction.provider_ref || (t ? t("nA") : "N/A")}</p>
              </div>
              {transaction.webhook_data && (
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-1">{t ? t("rawWebhookData") : "Raw Webhook Data"}</p>
                  <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs overflow-x-auto max-h-32">
                    {transaction.webhook_data}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Routing Info - Only for transactions */}
          {!isQRCode && transaction.routing_attempts > 1 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                {t ? t("routingAttempts") : "Routing Attempts"}: {transaction.routing_attempts}
              </p>
              {transaction.routing_history && (
                <pre className="mt-2 text-xs text-amber-700 overflow-x-auto">
                  {transaction.routing_history}
                </pre>
              )}
            </div>
          )}

          {/* Error Message - Only for transactions */}
          {!isQRCode && transaction.error_message && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-800">{t ? t("error") : "Error"}</p>
              <p className="text-sm text-red-700 mt-1">{transaction.error_message}</p>
              {transaction.error_code && (
                <p className="text-xs text-red-500 mt-1">{(t && t("errorCode")) || "Error Code"}: {transaction.error_code}</p>
              )}
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-slate-500 space-y-1 pt-4 border-t">
            <p>{t ? (t("created") || t("createdAt")) : "Created"}: {isQRCode 
              ? (transaction.createdAt ? format(new Date(transaction.createdAt), "MMM d, yyyy HH:mm:ss") : (t ? t("nA") : "N/A"))
              : (transaction.created_date ? format(new Date(transaction.created_date), "MMM d, yyyy HH:mm:ss") : (t ? t("nA") : "N/A"))
            }</p>
            <p>{t ? t("updated") : "Updated"}: {isQRCode
              ? (transaction.updatedAt ? format(new Date(transaction.updatedAt), "MMM d, yyyy HH:mm:ss") : (t ? t("nA") : "N/A"))
              : (transaction.updated_date ? format(new Date(transaction.updated_date), "MMM d, yyyy HH:mm:ss") : (t ? t("nA") : "N/A"))
            }</p>
            {!isQRCode && transaction.ip_address && <p>{t ? t("ip") : "IP"}: {transaction.ip_address}</p>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}